import React from "react";
import { BsPlusCircle, BsTrash } from "react-icons/bs";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";
import moment from "moment";

import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  getClosedDays,
  createClosedDay,
  deleteClosedDay,
  ClosedDayFilter,
} from "../../closedDaysSlice";

import "./ClosedDaysTab.scss";

const FILTERS: { value: ClosedDayFilter; label: string }[] = [
  { value: "upcoming", label: "Upcoming" },
  { value: "past", label: "Past" },
  { value: "all", label: "All" },
];

export function ClosedDaysTab() {
  const dispatch = useAppDispatch();
  const closedDays = useAppSelector((state) => state.closedDays.closedDays) || [];
  const getAPI = useAppSelector((state) => state.closedDays.getClosedDays);
  const createAPI = useAppSelector((state) => state.closedDays.createClosedDay);

  const [date, setDate] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [filter, setFilter] = React.useState<ClosedDayFilter>("upcoming");

  React.useEffect(() => {
    dispatch(getClosedDays(filter));
  }, [dispatch, filter]);

  React.useEffect(() => {
    if (!getAPI.loading && getAPI.error) {
      toast.error("Failed to load closed days. Please try again.");
    }
  }, [getAPI.loading, getAPI.error]);

  const canAdd = date !== "" && reason.trim() !== "" && !createAPI.loading;

  const onAdd = async () => {
    if (!canAdd) return;
    const result = await dispatch(createClosedDay({ date, reason: reason.trim() }));
    if (createClosedDay.fulfilled.match(result)) {
      toast.success("Closed day added.");
      setDate("");
      setReason("");
    } else {
      toast.error("Could not add that closed day. It may already exist.");
    }
  };

  const onRemove = async (id: string) => {
    const result = await dispatch(deleteClosedDay(id));
    if (deleteClosedDay.fulfilled.match(result)) {
      toast.success("Closed day removed.");
    } else {
      toast.error("Could not remove that closed day. Please try again.");
    }
  };

  return (
    <div className="tab-content closed-days-tab">
      <div className="tab-header">
        <h2>Closed Days</h2>
        <p className="subtitle">
          Dates customers can't book a delivery or pickup. The reason is internal —
          customers only see a "we're closed that day" message.
        </p>
      </div>

      <div className="add-row">
        <input
          type="date"
          className="field"
          aria-label="Closed date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <input
          type="text"
          className="field"
          aria-label="Reason"
          placeholder="Reason (e.g. Christmas)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <button
          type="button"
          className="add-button"
          disabled={!canAdd}
          onClick={onAdd}
        >
          <BsPlusCircle className="icon" />
          Add closed day
        </button>
      </div>

      <div className="filter-tabs" role="tablist" aria-label="Filter closed days">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            role="tab"
            aria-selected={filter === f.value}
            className={filter === f.value ? "active" : ""}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {getAPI.loading ? (
        <div className="loading-container">
          <Oval height={28} width={28} color="#1a73e8" secondaryColor="#d0d7de" />
        </div>
      ) : closedDays.length === 0 ? (
        <p className="empty-state">
          {filter === "upcoming"
            ? "No upcoming closed days."
            : filter === "past"
            ? "No past closed days."
            : "No closed days yet."}
        </p>
      ) : (
        <ul className="closed-days-list">
          {closedDays.map((c) => (
            <li key={c.id}>
              <span className="date">
                {moment(c.date, "YYYY-MM-DD").format("ddd, MMM D, YYYY")}
              </span>
              <span className="reason">{c.reason}</span>
              <button
                type="button"
                aria-label={`Remove ${c.date}`}
                className="remove-button"
                onClick={() => onRemove(c.id)}
              >
                <BsTrash className="icon" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
